import API from '../services/api';
import { interactiveServiceFetch } from '../services/interactiveServiceFetch';
import { INTERACTIVE_SERVICE_BASE_URL } from '../chatView/constants';

const api = new API();
const URL_AI_CONTEXT = 'api/ai-context';
const URL_AI_CONTEXT_VERSIONS = 'api/ai-context/versions';

// Reference-link access checks live in the interactive service (it owns the
// Google service-account credentials), reached via the same authenticated
// wrapper the chat uses.
const URL_VERIFY_LINKS = `${INTERACTIVE_SERVICE_BASE_URL}/reference-links/verify`;
const URL_REFERENCE_LINKS_META = `${INTERACTIVE_SERVICE_BASE_URL}/reference-links/meta`;

// Safety net so a hung verify request can never leave a row spinning forever.
const VERIFY_TIMEOUT_MS = 20000;

export const CONFLICT_ERROR = 'ai_context_conflict';

const normalizeConflictError = err => {
    const payload = err?.value;

    if (err?.status === 1 && payload && payload.error === 'conflict') {
        return {
            type: CONFLICT_ERROR,
            baseVersionId: payload.base_version_id ?? null,
            latestVersionId: payload.latest_version_id ?? null,
            latestCreatorEmail: payload.latest_creator_email ?? null,
            latestCreated: payload.latest_created ?? null,
            conflicts: payload.conflicts || [],
        };
    }

    return err;
};

const getContext = () =>
    new Promise((resolve, reject) => {
        api.getJson(URL_AI_CONTEXT, {
            method: 'GET',
            credentials: 'include',
            unique: true,
            disableBrowserCache: true,
        })
            .then(response =>
                resolve({
                    answers: response?.value?.answers || {},
                    maxLengths: response?.value?.max_lengths || {},
                    latestVersionId: response?.value?.latest_version_id ?? null,
                })
            )
            .catch(reject);
    });

const verifyReferenceLinks = async urls => {
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), VERIFY_TIMEOUT_MS);
    try {
        const response = await interactiveServiceFetch(URL_VERIFY_LINKS, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ urls }),
            signal: controller.signal,
        });
        if (!response.ok) {
            throw new Error(`Reference link verification failed (${response.status})`);
        }
        const data = await response.json();
        return { results: data?.results || [] };
    } finally {
        clearTimeout(timeoutId);
    }
};

const getReferenceLinksMeta = async () => {
    const response = await interactiveServiceFetch(URL_REFERENCE_LINKS_META, { method: 'GET' });
    if (!response.ok) {
        throw new Error(`Reference links meta failed (${response.status})`);
    }
    const data = await response.json();
    return { serviceAccountEmail: data?.service_account_email ?? null };
};

const saveAnswers = (answers, baseVersionId) =>
    new Promise((resolve, reject) => {
        api.getJson(URL_AI_CONTEXT, {
            method: 'POST',
            credentials: 'include',
            unique: true,
            fixErr: true,
            body: { answers, base_version_id: baseVersionId ?? null },
        })
            .then(response =>
                resolve({
                    answers: response?.value?.answers || {},
                    latestVersionId: response?.value?.latest_version_id ?? null,
                })
            )
            .catch(err => reject(normalizeConflictError(err)));
    });

const listVersions = () =>
    new Promise((resolve, reject) => {
        api.getJson(URL_AI_CONTEXT_VERSIONS, {
            method: 'GET',
            credentials: 'include',
            unique: true,
            disableBrowserCache: true,
        })
            .then(response => resolve(response?.value?.versions || []))
            .catch(reject);
    });

const getVersion = versionId =>
    new Promise((resolve, reject) => {
        api.getJson(`${URL_AI_CONTEXT_VERSIONS}/${versionId}`, {
            method: 'GET',
            credentials: 'include',
            unique: true,
            disableBrowserCache: true,
        })
            .then(response => resolve(response?.value?.answers || {}))
            .catch(reject);
    });

const restoreVersion = (versionId, baseVersionId) =>
    new Promise((resolve, reject) => {
        api.getJson(`${URL_AI_CONTEXT_VERSIONS}/${versionId}/restore`, {
            method: 'POST',
            credentials: 'include',
            unique: true,
            fixErr: true,
            body: { base_version_id: baseVersionId ?? null },
        })
            .then(response =>
                resolve({
                    answers: response?.value?.answers || {},
                    latestVersionId: response?.value?.latest_version_id ?? null,
                })
            )
            .catch(err => reject(normalizeConflictError(err)));
    });

export default {
    getContext,
    saveAnswers,
    listVersions,
    getVersion,
    restoreVersion,
    verifyReferenceLinks,
    getReferenceLinksMeta,
};
